<template>
  <div class="books__filters">
    <div class="books__inputs" :class="{ inModal }">
      <e-date-picker v-model="filters.residenceDate" label="Период проживания" partial-multiple :multiple="true" />

      <e-select
        v-model="filters.account"
        label="Аккаунт"
        clearable
        item-value="contractNumber"
        :items="contractsItems"
      />

      <e-select
        v-model="filters.tariff"
        label="Тарифный план"
        clearable
        item-value="tariffScheduleCid"
        :disabled="!filters.account"
        :items="tariffsItems"
      />

      <e-date-picker
        v-model="filters.bookDate"
        label="Период бронирования"
        partial-multiple 
        :multiple="true" 
        clearable
        :disabled="disabledClientSideFilters"
      />

      <e-select
        v-model="filters.roomCategory"
        label="Категории номеров"
        item-title="name[0].value"
        clearable
        item-value="cid"
        :disabled="disabledClientSideFilters"
        :items="roomCategoriesItems"
      />
      <e-select
        v-model="filters.status"
        label="Статус"
        variant="outlined"
        clearable
        :disabled="disabledClientSideFilters"
        :items="statusFiltersItems"
      />
    </div>
    <div class="books__checkbox">
      <e-checkbox
        v-model="filters.checkRequired"
        :disabled="disabledClientSideFilters"
        label="Требуют обработки"
        class="with-badge"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { computed, ref, toRef, watch } from 'vue';
import { ECheckbox, EDatePicker, ESelect } from '@/core/components/ui';
import { getSelectFilterItems2 } from '@/core/utils/filters';
import { objectKeys } from '@/core/utils/object';
import { type BookRoomCategory, useBooksStore } from '@/parts/books/store/books';
import { BOOK_STATUSES, type BooksFilters } from '@/parts/books/utils/consts';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import { type Tariffs, useTariffsStore } from '@/parts/tariffs/store/tariffs';

const props = defineProps<{
  filters: BooksFilters;

  inModal?: boolean;
}>();

const { books } = useBooksStore();
const { accommodationObject } = useAccommodationObjectsStore();
const { loadTariffs } = useTariffsStore();

const filters = useVModel(props, 'filters');

const tariffs = ref<Tariffs>();

watch(
  () => filters.value.account,
  async () => {
    if (filters.value.account) {
      const result = await loadTariffs(filters.value.account);

      if (result.data) {
        tariffs.value = result.data.tariffSchedules2;
      }
    }
  },
  {
    immediate: true,
  },
);

const disabledClientSideFilters = toRef(() => !books.value.loaded || !books.value.data.quotaOrders.length);

const contractsItems = computed(() => {
  return (
    accommodationObject.value?.contracts.map((contract) => ({
      ...contract,
      title: contract.contractNumber,
    })) || []
  );
});

const tariffsItems = computed(() => {
  if (!tariffs.value) {
    return [];
  }

  const tariffsList = [
    ...tariffs.value.extranet,
    ...tariffs.value.remote
      .filter((x) => x.tariffSchedule)
      .map((x) => ({
        tariffScheduleCid: x.tariffSchedule?.tariffScheduleCid,
        name: x.name,
      })),
  ];

  return (
    tariffsList.map((tariff) => ({
      ...tariff,
      tariffScheduleCid: tariff.tariffScheduleCid,
      title: tariff.name[0].value,
    })) || []
  );
});

const roomCategoriesItems = computed<BookRoomCategory[]>(() => {
  if (!books.value.loaded || disabledClientSideFilters.value) {
    return [] as BookRoomCategory[];
  }

  return getSelectFilterItems2(books.value.data.quotaOrders, 'roomCategory') as BookRoomCategory[];
});

const statusFiltersItems = toRef(() => {
  const keys = objectKeys(BOOK_STATUSES);

  return keys.map((key) => ({
    title: BOOK_STATUSES[key],
    value: key,
  }));
});
</script>

<style lang="scss" scoped>
.books {
  &__filters {
    display: flex;
    align-items: flex-start;

    gap: $gap-4;

    @include md-and-down {
      flex-direction: column;

      gap: $gap-2;
    }
  }

  &__inputs {
    display: grid;

    grid-gap: 16px;
    grid-template-columns: 1fr 1fr 1fr;

    width: 100%;

    &.inModal {
      grid-template-columns: 1fr;
    }

    & > div {
      flex: initial;
    }
  }

  &__checkbox {
    flex-grow: 1;

    margin-top: $gap-3;
  }
}
</style>
